import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tenant, TenantFormData, TenantAdminInvitation, TenantDeletionRequest, TenantStatus } from "@/types/tenant";
import { useToast } from "@/hooks/use-toast";

export function useTenants() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const tenantsQuery = useQuery({
    queryKey: ["tenants"],
    queryFn: async (): Promise<Tenant[]> => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      
      return (data || []).map(customer => ({
        ...customer,
        tenant_status: (customer.tenant_status as TenantStatus) || 'active',
        device_count: customer.device_count || 0,
      }));
    },
  });

  const tenantByIdQuery = (id: string) => useQuery({
    queryKey: ["tenant", id],
    queryFn: async (): Promise<Tenant | null> => {
      const { data, error } = await supabase
        .from("customers")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      
      return data ? {
        ...data,
        tenant_status: (data.tenant_status as TenantStatus) || 'active',
        device_count: data.device_count || 0,
      } : null;
    },
    enabled: !!id,
  });

  const createTenantMutation = useMutation({
    mutationFn: async (formData: TenantFormData) => {
      const { data: userData } = await supabase.auth.getUser();
      
      // Create the customer record
      const { data: customer, error: customerError } = await supabase
        .from("customers")
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zip: formData.zip,
          country: formData.country,
          industry: formData.industry,
          company_size: formData.company_size,
          expected_device_count: formData.expected_device_count || 0,
          billing_contact_name: formData.billing_same_as_primary ? formData.name : formData.billing_contact_name,
          billing_contact_email: formData.billing_same_as_primary ? formData.email : formData.billing_contact_email,
          logo_url: formData.logo_url,
          tenant_status: 'creating',
          status: 'pending',
          tenant_type: formData.tenant_type || 'customer',
          can_parent_manage: formData.can_parent_manage ?? true,
          onboarded_by: userData.user?.id,
          onboarded_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (customerError) throw customerError;

      // Get or create partner record and link customer
      if (userData.user) {
        const { data: existingPartner } = await supabase
          .from("partners")
          .select("id")
          .eq("user_id", userData.user.id)
          .single();

        let partnerId = existingPartner?.id;

        if (!partnerId) {
          const { data: newPartner, error: partnerError } = await supabase
            .from("partners")
            .insert({
              user_id: userData.user.id,
              email: userData.user.email || '',
              name: userData.user.email?.split('@')[0] || 'Partner',
            })
            .select()
            .single();

          if (partnerError) throw partnerError;
          partnerId = newPartner.id;
        }

        // Set parent_id to partner's own customer record if it exists
        const { data: partnerCustomer } = await supabase
          .from("partner_customers")
          .select("customer_id")
          .eq("partner_id", partnerId)
          .limit(1);

        if (partnerCustomer && partnerCustomer.length > 0) {
          // Update the newly created tenant's parent_id
          await supabase
            .from("customers")
            .update({ parent_id: partnerCustomer[0].customer_id })
            .eq("id", customer.id);
        }

        // Link customer to partner
        const { error: linkError } = await supabase
          .from("partner_customers")
          .insert({
            partner_id: partnerId,
            customer_id: customer.id,
          });

        if (linkError) throw linkError;
      }

      // Create admin invitation if requested
      if (formData.invite_admin && formData.admin_email) {
        const { error: inviteError } = await supabase
          .from("tenant_admin_invitations")
          .insert({
            customer_id: customer.id,
            email: formData.admin_email,
            name: formData.admin_name,
            invited_by: userData.user?.id,
          });

        if (inviteError) {
          console.error("Failed to create admin invitation:", inviteError);
        }
      }

      return customer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast({
        title: "Tenant Created",
        description: "The new tenant has been successfully onboarded.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to create tenant: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateTenantMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<TenantFormData> }) => {
      const { data: updated, error } = await supabase
        .from("customers")
        .update({
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          city: data.city,
          state: data.state,
          zip: data.zip,
          country: data.country,
          industry: data.industry,
          company_size: data.company_size,
          expected_device_count: data.expected_device_count,
          billing_contact_name: data.billing_contact_name,
          billing_contact_email: data.billing_contact_email,
          logo_url: data.logo_url,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast({
        title: "Tenant Updated",
        description: "The tenant has been successfully updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to update tenant: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const updateTenantStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: TenantStatus }) => {
      const { data, error } = await supabase
        .from("customers")
        .update({ tenant_status: status })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
    },
  });

  const licenseUsageQuery = useQuery({
    queryKey: ["tenant-license-usage"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("customers")
        .select("id, name, device_count, expected_device_count, tenant_status");

      if (error) throw error;

      const totalDevices = (data || []).reduce((sum, c) => sum + (c.device_count || 0), 0);
      const activeTenants = (data || []).filter(c => c.tenant_status === 'active').length;
      const pendingTenants = (data || []).filter(c => 
        c.tenant_status === 'creating' || c.tenant_status === 'incomplete'
      ).length;

      return {
        totalDevices,
        activeTenants,
        pendingTenants,
        tenantUsage: data || [],
      };
    },
  });

  return {
    tenants: tenantsQuery.data || [],
    isLoading: tenantsQuery.isLoading,
    error: tenantsQuery.error,
    refetch: tenantsQuery.refetch,
    getTenantById: tenantByIdQuery,
    createTenant: createTenantMutation.mutateAsync,
    isCreating: createTenantMutation.isPending,
    updateTenant: updateTenantMutation.mutateAsync,
    isUpdating: updateTenantMutation.isPending,
    updateTenantStatus: updateTenantStatusMutation.mutateAsync,
    licenseUsage: licenseUsageQuery.data,
    isLoadingLicenseUsage: licenseUsageQuery.isLoading,
  };
}

export function useTenantInvitations(customerId?: string) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const invitationsQuery = useQuery({
    queryKey: ["tenant-invitations", customerId],
    queryFn: async (): Promise<TenantAdminInvitation[]> => {
      let query = supabase.from("tenant_admin_invitations").select("*");
      
      if (customerId) {
        query = query.eq("customer_id", customerId);
      }
      
      const { data, error } = await query.order("created_at", { ascending: false });
      if (error) throw error;
      return data as TenantAdminInvitation[];
    },
    enabled: true,
  });

  const cancelInvitationMutation = useMutation({
    mutationFn: async (invitationId: string) => {
      const { error } = await supabase
        .from("tenant_admin_invitations")
        .update({ status: 'cancelled' })
        .eq("id", invitationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-invitations"] });
      toast({
        title: "Invitation Cancelled",
        description: "The admin invitation has been cancelled.",
      });
    },
  });

  return {
    invitations: invitationsQuery.data || [],
    isLoading: invitationsQuery.isLoading,
    cancelInvitation: cancelInvitationMutation.mutateAsync,
  };
}

export function useTenantDeletions() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const deletionsQuery = useQuery({
    queryKey: ["tenant-deletions"],
    queryFn: async (): Promise<TenantDeletionRequest[]> => {
      const { data, error } = await supabase
        .from("tenant_deletion_requests")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as TenantDeletionRequest[];
    },
  });

  const requestDeletionMutation = useMutation({
    mutationFn: async ({ customerId, reason }: { customerId: string; reason: string }) => {
      const { data: userData } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from("tenant_deletion_requests")
        .insert({
          customer_id: customerId,
          requested_by: userData.user?.id,
          reason,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-deletions"] });
      queryClient.invalidateQueries({ queryKey: ["tenants"] });
      toast({
        title: "Deletion Requested",
        description: "Your deletion request has been submitted for approval.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: `Failed to submit deletion request: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  return {
    deletions: deletionsQuery.data || [],
    isLoading: deletionsQuery.isLoading,
    requestDeletion: requestDeletionMutation.mutateAsync,
    isRequesting: requestDeletionMutation.isPending,
  };
}
